
import { computed, defineComponent, onMounted, PropType, ref } from 'vue';
import { useStore } from 'vuex';
import { key } from '@/store';
import { apiEditCategory, apiGetCategories, Category } from '@/api/categoryApi';
import { ACTIVITY_ID, ATTACHMENTS_ID } from '@/core/constants';
import NewConfirmModal from '@/components/NewConfirmModal.vue';
import { IQuiz } from '@/types/quiz';

export default defineComponent({
  name: 'QuestionCategoryHeader',
  components: { NewConfirmModal },
  props: {
    requiredUnansweredQuestions: {
      type: Object as PropType<IQuiz['validation_errors']>,
      default: () => ({}),
    },
  },
  emits: ['navigate-to-required-question'],
  setup(props, ctx) {
    const store = useStore(key);
    const newQuestionPositionIndex = computed(() => store.state.createQuestion.questionPositionIndex);
    const editedCategoryName = ref<{
      id: number,
      name: string,
    }>({
      id: -1,
      name: ''
    });
    const isConfirmEditVisible = ref(false);
    const isRequiredDropdownOpen = ref(false);

    const onRequiredQuestionClick = (id: string) => {
      isRequiredDropdownOpen.value = false;
      ctx.emit('navigate-to-required-question', parseInt(id, 10));
    };

    onMounted(async () => {
      try {
        const categories = await apiGetCategories();
        updateCategoriesInStore(categories);
        if (newQuestionPositionIndex.value === null) { // wouldn't choose default category in case it's going back from create question page
          setInitialCurrentCategory(categories);
        }
      } catch (e) {
        throw new Error('Failing Getting categories');
      }
    });

    const updateCategoriesInStore = (categories: Category[]) => {
      store.commit('setCategories', categories);
    };

    const setInitialCurrentCategory = (categories: Category[]) => {
      store.commit('setCurrentCategory', categories[0].id);
    };

    const categories = computed((): Category[] => store.state.categories);
    const currentCategory = computed(() => store.state.currentCategory);
    const isEditingCategory = computed(() => store.state.isEditingCategory);

    const openCategory = (categoryId: number | null) => {
      if (isEditingCategory.value) {
        return;
      }
      window.scrollTo(0, 0);
      store.commit('setCurrentCategory', categoryId);
    };

    const onEditMenu = () => {
      const category = categories.value.filter(c => c.id === currentCategory.value)[0];

      editedCategoryName.value = {
        id: category.id,
        name: category.name
      };
      store.commit('setIsEditingCategory', true);
    };

    const cancelEditing = () => {
      editedCategoryName.value = {
        id: -1,
        name: 'category.name'
      };
      store.commit('setIsEditingCategory', false);
    };

    const handleEditing = async (category: Category) => {
      if (editedCategoryName.value.name === categories.value.find(el => el.id === editedCategoryName.value.id)?.name) {
        cancelEditingConfirmModal();
      } else {
        isConfirmEditVisible.value = true;
      }

    };
    const cancelEditingConfirmModal = () => {
      isConfirmEditVisible.value = false;
      cancelEditing();
    };
    const confirmCategoryEditModal = async () => {
      try {
        await apiEditCategory({
          id: editedCategoryName.value.id,
          name: editedCategoryName.value.name
        });
        const categories = await apiGetCategories();
        updateCategoriesInStore(categories);
      } catch (e) {
        throw new Error('Edit category fail');
      } finally {
        cancelEditing();
        isConfirmEditVisible.value = false;
      }
    };

    const openActivity = () => {
      openCategory(ACTIVITY_ID);
    };

    const openAttachments = () => {
      openCategory(ATTACHMENTS_ID);
    };

    return {
      categories,
      currentCategory,
      isEditingCategory,
      openCategory,
      onEditMenu,
      cancelEditing,
      handleEditing,
      editedCategoryName,
      isConfirmEditVisible,
      cancelEditingConfirmModal,
      confirmCategoryEditModal,
      store,
      openActivity,
      ACTIVITY_ID,
      openAttachments,
      ATTACHMENTS_ID,
      isRequiredDropdownOpen,
      onRequiredQuestionClick
    };
  }
});
